// Asset-Brücke zwischen Blender und dem Client.
//
// Grundhaltung: **Assets sind eine Verbesserung, kein Fundament.** Jedes System
// baut seine Geometrie aus Code, und dieser Loader tauscht sie gegen ein
// glTF-Modell aus, *wenn* eines vorhanden ist. Damit ist das Spiel zu jedem
// Zeitpunkt spielbar — auch wenn kein einziges Modell fertig ist, wenn ein
// Export kaputt ist oder wenn jemand ohne den assets-Ordner klont.
//
// Der zweite Grund für diese Trennung: Konzept §12.4 verlangt, dass Assets aus
// verschiedenen Quellen zusammenpassen. Ein Loader, der jedes Modell gegen die
// erwarteten Maße aus `shared/consist.js` prüft und bei Abweichung ablehnt,
// erzwingt diese Konsistenz maschinell statt per Zuruf.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

const MANIFEST_URL = '/assets/models/manifest.json';
const MODEL_BASE = '/assets/models/';

// Wie stark ein Modell von den erwarteten Maßen abweichen darf, bevor es
// abgelehnt wird. 12 cm: großzügig genug für Rundungen und Griffe, die über
// die Kontur hinausragen, streng genug um einen Maßstabsfehler (Faktor 100,
// der klassische Blender-Fehler) sicher zu fangen.
const SIZE_TOLERANCE = 0.12;

export class AssetLibrary {
  constructor() {
    this.manifest = null;
    this.models = new Map();      // name -> { scene, parts, paintSurfaces, tris }
    this.rejected = new Map();    // name -> Grund
    this.available = false;
    this.loader = new GLTFLoader();
    this._loading = new Map();
  }

  /**
   * Lädt das Manifest. Fehlt es, bleibt die Bibliothek leer und alle Abfragen
   * liefern null — genau das ist der Normalzustand am Anfang eines Projekts.
   */
  async init() {
    try {
      const res = await fetch(MANIFEST_URL, { cache: 'no-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.manifest = await res.json();
      const entries = Object.keys(this.manifest.assets ?? {});
      this.available = entries.length > 0;
      console.log(`[assets] Manifest gelesen: ${entries.length} Assets angekündigt.`);
    } catch (err) {
      console.log(`[assets] Kein Manifest (${err.message}) — Code-Geometrie wird benutzt.`);
      this.manifest = null;
      this.available = false;
    }
    return this;
  }

  /** Namen aller im Manifest angekündigten Assets. */
  names() {
    return Object.keys(this.manifest?.assets ?? {});
  }

  has(name) {
    return !!this.manifest?.assets?.[name] && !this.rejected.has(name);
  }

  /**
   * Lädt ein Modell und prüft es. `expectedSize` ist optional
   * ({ length, width, height }) — passt das Modell nicht, wird es abgelehnt
   * und der Aufrufer benutzt weiter seine Code-Geometrie.
   */
  async load(name, expectedSize = null) {
    if (this.models.has(name)) return this.models.get(name);
    if (this.rejected.has(name)) return null;
    if (this._loading.has(name)) return this._loading.get(name);

    const entry = this.manifest?.assets?.[name];
    if (!entry) return null;

    const promise = this.#loadOne(name, entry, expectedSize);
    this._loading.set(name, promise);
    const result = await promise;
    this._loading.delete(name);
    return result;
  }

  async #loadOne(name, entry, expectedSize) {
    const file = entry.file ?? `${name}.glb`;
    try {
      const gltf = await this.loader.loadAsync(MODEL_BASE + file);
      const scene = gltf.scene;

      // Maße prüfen. Ein Modell im falschen Maßstab ist schlimmer als kein
      // Modell: es sieht auf den ersten Blick richtig aus und zerlegt dann
      // Kollision, Decks und Greifpunkte.
      const bbox = new THREE.Box3().setFromObject(scene);
      const size = bbox.getSize(new THREE.Vector3());
      if (expectedSize) {
        const problems = [];
        // Länge liegt in Z (Fahrtrichtung), Breite in X, Höhe in Y.
        if (Math.abs(size.z - expectedSize.length) > SIZE_TOLERANCE) {
          problems.push(`Länge ${size.z.toFixed(2)} statt ${expectedSize.length}`);
        }
        if (Math.abs(size.x - expectedSize.width) > SIZE_TOLERANCE) {
          problems.push(`Breite ${size.x.toFixed(2)} statt ${expectedSize.width}`);
        }
        if (expectedSize.height != null &&
            Math.abs(size.y - expectedSize.height) > SIZE_TOLERANCE * 2) {
          problems.push(`Höhe ${size.y.toFixed(2)} statt ${expectedSize.height}`);
        }
        if (problems.length > 0) {
          this.rejected.set(name, problems.join(', '));
          console.warn(`[assets] "${name}" abgelehnt: ${problems.join(', ')}`);
          return null;
        }
      }

      // Benannte Teile einsammeln: part_* sind beweglich, paint_* sind
      // Malflächen. Das Namensschema ist der Vertrag mit dem Asset-Bau.
      const parts = new Map();
      const paintSurfaces = new Map();
      let tris = 0;
      scene.traverse((obj) => {
        if (obj.isMesh) {
          obj.castShadow = true;
          obj.receiveShadow = true;
          const geo = obj.geometry;
          if (geo?.index) tris += geo.index.count / 3;
          else if (geo?.attributes?.position) tris += geo.attributes.position.count / 3;
        }
        if (obj.name.startsWith('part_')) parts.set(obj.name.slice(5), obj);
        if (obj.name.startsWith('paint_')) paintSurfaces.set(obj.name.slice(6), obj);
      });

      const record = { name, scene, parts, paintSurfaces, tris: Math.round(tris), size };
      this.models.set(name, record);
      console.log(
        `[assets] "${name}" geladen: ${record.tris} Dreiecke, ` +
        `${parts.size} Teile, ${paintSurfaces.size} Malflächen`
      );
      return record;
    } catch (err) {
      this.rejected.set(name, err.message);
      console.warn(`[assets] "${name}" nicht ladbar: ${err.message}`);
      return null;
    }
  }

  /**
   * Eine benutzbare Kopie eines geladenen Modells.
   * Wichtig: `clone()` teilt Geometrien und Materialien — das ist gewollt, denn
   * zehn Waggons sollen nicht zehnmal dieselben Buffer im Speicher halten.
   * Wer ein eigenes Material braucht (Malflächen!), muss es selbst ersetzen.
   */
  instance(name) {
    const record = this.models.get(name);
    if (!record) return null;
    const clone = record.scene.clone(true);
    // Die Teile-Verweise müssen auf den Klon zeigen, nicht auf das Original.
    const parts = new Map();
    const paintSurfaces = new Map();
    clone.traverse((obj) => {
      if (obj.name.startsWith('part_')) parts.set(obj.name.slice(5), obj);
      if (obj.name.startsWith('paint_')) paintSurfaces.set(obj.name.slice(6), obj);
    });
    return { root: clone, parts, paintSurfaces, size: record.size };
  }

  /** Lädt mehrere Assets parallel; fehlende werden stillschweigend übergangen. */
  async loadMany(requests) {
    const results = await Promise.all(
      requests.map(({ name, expectedSize }) => this.load(name, expectedSize))
    );
    return results.filter(Boolean).length;
  }

  stats() {
    return {
      announced: this.names().length,
      loaded: this.models.size,
      rejected: this.rejected.size,
      triangles: [...this.models.values()].reduce((a, m) => a + m.tris, 0),
    };
  }
}

/** Eine Bibliothek für den ganzen Client. */
export const assets = new AssetLibrary();

/**
 * System-Wrapper, damit die Engine das Laden vor allen anderen Systemen
 * abschließt. Muss als erstes registriert werden — Systeme, die Modelle
 * benutzen wollen, fragen danach in ihrem eigenen init().
 */
export class AssetSystem {
  async init(ctx) {
    await assets.init();
    ctx.assets = assets;
  }
}
